import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { URLS } from '../helpers/URLS';
import { measureImage } from '../helpers/chatMedia';

const ChatContext = createContext(null);

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) throw new Error('useChat must be used inside a ChatProvider');
    return context;
};

const BASE = '/api/v1/';

/* Chat calls do not go through useAxios on purpose: that hook drives the
   global page loader, and a background poll or a read receipt firing the
   full-screen spinner would make the whole app flicker while someone types. */
const request = async (method, url, body) => {
    try {
        const res = await axios[method](BASE + url, body);
        return res.data;
    } catch (error) {
        const payload = error && error.response && error.response.data;
        if (payload && typeof payload === 'object') return payload;
        return { status: 0, success: false, message: 'Cannot reach the server' };
    }
};

/* How long to wait for the server to acknowledge something sent over the
   socket before giving up on it and going over HTTP instead.

   A socket that is connected is not a promise that anything is listening at
   the other end — an event can be handed to a server that has no handler for
   it yet and is simply dropped, with no error and no reply. Without a
   deadline the sender waits for ever on a spinner. */
const ACK_TIMEOUT_MS = 6000;

/* Emit and wait for the acknowledgement, resolving null if none comes.
   Callers treat null as "the socket did not work out, use HTTP". */
const emitWithAck = (socket, event, payload) => new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
    };
    const timer = setTimeout(() => finish(null), ACK_TIMEOUT_MS);
    socket.emit(event, payload, (ack) => {
        clearTimeout(timer);
        finish(ack || null);
    });
});

/* Every browser tab needs its own id for an optimistic message, so the ack
   can be matched back to the bubble that is already on screen. */
let clientSeq = 0;
const nextClientId = () => `${Date.now().toString(36)}-${(clientSeq += 1)}`;

/* How many chat windows may sit open at the bottom of the screen at once.
   Beyond this the oldest is dropped, so they never march across the page. */
const MAX_OPEN_WINDOWS = 3;

const idOf = (message) => String(message._id || message.clientId || '');

/* Fold a freshly fetched page into whatever the thread already holds.

   A fetch and a live message can be in flight at the same time, and simply
   taking the server's answer would drop a message that arrived while the
   request was open. Merging keeps both, and de-duplicates on the way. */
const mergeMessages = (existing = [], incoming = []) => {
    const byId = new Map();
    [...existing, ...incoming].forEach((message) => {
        const key = idOf(message);
        if (!key) return;
        /* The later write wins, so a saved message replaces its own
           optimistic copy rather than sitting beside it. */
        byId.set(key, { ...(byId.get(key) || {}), ...message });
    });

    /* Also drop any optimistic copy whose saved version came back under a
       different id but the same clientId. */
    const savedClientIds = new Set(
        [...byId.values()]
            .filter((m) => m.clientId && !String(m._id).startsWith('pending-'))
            .map((m) => m.clientId),
    );

    return [...byId.values()]
        .filter((m) => !(String(m._id).startsWith('pending-') && savedClientIds.has(m.clientId)))
        /* A deleted message comes back with its text and attachment fields
           simply absent rather than emptied, so a merge would leave the old
           words sitting underneath and the bubble would go on showing
           something that has been taken back. Cleared explicitly here, which
           covers a live delete and a refetch after one alike. */
        .map((m) => (m.deleted
            ? { ...m, text: '', attachment: undefined, replyTo: undefined, previewUrl: '' }
            : m))
        .sort((a, b) => {
            const at = new Date(a.createdAt) - new Date(b.createdAt);
            return at !== 0 ? at : String(a._id).localeCompare(String(b._id));
        });
};

export const ChatProvider = ({ user, children }) => {
    const myCode = user?.employeeCode || '';

    const [connected, setConnected] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [presence, setPresence] = useState({});
    /* conversationId -> { messages, hasMore, loading, loaded } */
    const [threads, setThreads] = useState({});
    /* peerCode -> true while they are typing to me */
    const [typingBy, setTypingBy] = useState({});
    /* the popup windows along the bottom of the screen */
    const [windows, setWindows] = useState([]);

    const socketRef = useRef(null);
    /* Which threads have been fetched, or are being fetched right now.

       This has to be a ref, not state: the guard is read on the very next
       line after it is set, and a state updater does not run synchronously —
       reading it back immediately would always see the old value and every
       load would be skipped. */
    const threadStateRef = useRef({});
    /* The thread the full page is showing. A message for it must never pop a
       window open on top of the page the employee is already reading. */
    const activeConversationRef = useRef(null);
    const windowsRef = useRef([]);
    const typingTimersRef = useRef({});
    const sentTypingAtRef = useRef(0);
    /* When each thread was last marked read here.

       Clearing a badge and refreshing the list race each other: the refresh
       is usually already in flight when the read is sent, so its reply still
       carries the old count and would put the badge back on a conversation
       the employee is looking at. A conversation read in the last few seconds
       therefore keeps the local zero. */
    const readAtRef = useRef({});

    useEffect(() => { windowsRef.current = windows; }, [windows]);

    const unreadTotal = useMemo(
        () => conversations.reduce((sum, c) => sum + (c.unread || 0), 0),
        [conversations],
    );

    /* ------------------------- loading ------------------------- */

    const refreshConversations = useCallback(async () => {
        const res = await request('get', URLS.chatConversations);
        if (!res || res.status !== 200) return;

        const now = Date.now();
        setConversations((res.data || []).map((row) => {
            const readAt = readAtRef.current[row.conversationId];
            return readAt && now - readAt < 5000 ? { ...row, unread: 0 } : row;
        }));
    }, []);

    const loadThread = useCallback(async (conversationId, { force = false } = {}) => {
        if (!conversationId) return;

        const state = threadStateRef.current[conversationId];
        if (!force && (state === 'loading' || state === 'loaded')) return;
        threadStateRef.current[conversationId] = 'loading';

        setThreads((prev) => ({
            ...prev,
            [conversationId]: { messages: [], hasMore: false, ...(prev[conversationId] || {}), loading: true },
        }));

        const res = await request('get', URLS.chatMessages(conversationId));
        const ok = res && res.status === 200;
        threadStateRef.current[conversationId] = ok ? 'loaded' : 'idle';

        setThreads((prev) => {
            const current = prev[conversationId] || { messages: [] };
            return {
                ...prev,
                [conversationId]: {
                    messages: ok ? mergeMessages(current.messages, res.data.messages) : current.messages,
                    hasMore: ok ? Boolean(res.data.hasMore) : Boolean(current.hasMore),
                    loading: false,
                    loaded: ok,
                },
            };
        });
    }, []);

    const loadOlder = useCallback(async (conversationId) => {
        const thread = threads[conversationId];
        if (!thread || !thread.hasMore || thread.loading) return;
        const oldest = thread.messages[0];
        if (!oldest) return;

        setThreads((prev) => ({ ...prev, [conversationId]: { ...prev[conversationId], loading: true } }));
        const res = await request('get', URLS.chatMessages(conversationId, oldest._id));
        setThreads((prev) => {
            const current = prev[conversationId] || { messages: [] };
            if (!res || res.status !== 200) return { ...prev, [conversationId]: { ...current, loading: false } };
            return {
                ...prev,
                [conversationId]: {
                    ...current,
                    messages: mergeMessages(current.messages, res.data.messages),
                    hasMore: res.data.hasMore,
                    loading: false,
                },
            };
        });
    }, [threads]);

    /* ------------------------- read state ------------------------- */

    const markRead = useCallback((conversationId) => {
        if (!conversationId) return;
        readAtRef.current[conversationId] = Date.now();
        /* Cleared here as well as on the server so the badge goes the moment
           the thread is opened, rather than after a round trip. */
        setConversations((prev) => prev.map((c) => (
            c.conversationId === conversationId ? { ...c, unread: 0 } : c
        )));
        setWindows((prev) => prev.map((w) => (
            w.conversationId === conversationId ? { ...w, unread: 0 } : w
        )));

        const socket = socketRef.current;
        if (socket && socket.connected) socket.emit('chat:read', { conversation: conversationId });
        else request('post', URLS.chatMarkRead(conversationId));
    }, []);

    const setActiveConversation = useCallback((conversationId) => {
        activeConversationRef.current = conversationId || null;
        if (conversationId) markRead(conversationId);
    }, [markRead]);

    /* ------------------------- windows ------------------------- */

    const closeWindow = useCallback((conversationId) => {
        setWindows((prev) => prev.filter((w) => w.conversationId !== conversationId));
    }, []);

    const minimiseWindow = useCallback((conversationId, minimised) => {
        setWindows((prev) => prev.map((w) => (
            w.conversationId === conversationId ? { ...w, minimised } : w
        )));
        if (!minimised) markRead(conversationId);
    }, [markRead]);

    /* Bring up a chat with someone, creating the thread if this is the first
       time. Used by the team list, the recent list and the popup alike. */
    const openChatWith = useCallback(async (peer, { asWindow = true } = {}) => {
        if (!peer || !peer.employeeCode) return null;

        const res = await request('post', URLS.chatOpenConversation, { peerCode: peer.employeeCode });
        if (!res || res.status !== 200) return null;

        const { conversationId } = res.data;
        const person = { ...peer, ...res.data.peer };

        if (asWindow) {
            setWindows((prev) => {
                const already = prev.find((w) => w.conversationId === conversationId);
                if (already) {
                    return prev.map((w) => (w.conversationId === conversationId
                        ? { ...w, minimised: false, unread: 0 } : w));
                }
                const next = [...prev, { conversationId, peer: person, minimised: false, unread: 0 }];
                return next.slice(-MAX_OPEN_WINDOWS);
            });
        }

        loadThread(conversationId);
        markRead(conversationId);
        refreshConversations();
        return { conversationId, peer: person };
    }, [loadThread, markRead, refreshConversations]);

    /* ------------------------- sending ------------------------- */

    const sendMessage = useCallback(async (conversationId, peerCode, text, options = {}) => {
        const body = String(text || '').trim();
        const { attachment = null, replyTo = null, previewUrl = '' } = options;
        if ((!body && !attachment) || !conversationId || !peerCode) return;

        const clientId = nextClientId();
        const optimistic = {
            _id: `pending-${clientId}`,
            clientId,
            conversation: conversationId,
            senderCode: myCode,
            receiverCode: peerCode,
            text: body,
            kind: attachment ? 'image' : 'text',
            attachment: attachment || undefined,
            /* The blob the browser already holds, so the picture is in the
               thread the instant send is pressed rather than after a round
               trip to fetch back the bytes that just went up. */
            previewUrl: previewUrl || '',
            replyTo: replyTo ? {
                messageId: replyTo._id,
                senderCode: replyTo.senderCode,
                senderName: replyTo.senderName,
                text: replyTo.deleted ? 'This message was deleted'
                    : (replyTo.text || (replyTo.kind === 'image' ? '\uD83D\uDCF7 Photo' : '')),
                kind: replyTo.kind === 'image' ? 'image' : 'text',
            } : undefined,
            status: 'pending',
            createdAt: new Date().toISOString(),
        };

        /* Shown immediately, then replaced when the server answers. The
           clientId is what ties the two together, so the bubble is swapped
           rather than a second one appearing beside it. */
        setThreads((prev) => {
            const current = prev[conversationId] || { messages: [], hasMore: false };
            return { ...prev, [conversationId]: { ...current, messages: [...current.messages, optimistic] } };
        });

        const settle = (saved, failed) => {
            setThreads((prev) => {
                const current = prev[conversationId];
                if (!current) return prev;
                return {
                    ...prev,
                    [conversationId]: {
                        ...current,
                        messages: current.messages.map((m) => {
                            if (m.clientId !== clientId || !String(m._id).startsWith('pending-')) return m;
                            if (failed) return { ...m, status: 'failed' };
                            /* The local blob is carried across on purpose: the
                               saved copy points at a URL the browser has not
                               fetched yet, and swapping straight to it would
                               blank the picture the sender is already looking
                               at. It is released once the real one loads. */
                            return { ...saved, clientId, previewUrl: m.previewUrl || '' };
                        }),
                    },
                };
            });
        };

        const wire = {
            peerCode,
            text: body,
            clientId,
            attachment: attachment || undefined,
            replyTo: replyTo ? String(replyTo._id) : undefined,
        };

        const socket = socketRef.current;
        const ack = socket && socket.connected
            ? await emitWithAck(socket, 'chat:send', wire)
            : null;

        if (ack && ack.success) {
            settle(ack.data, false);
        } else if (ack) {
            /* The server answered and refused — sending it again over HTTP
               would only be refused for the same reason. */
            settle(null, true);
        } else {
            /* No socket, or nothing came back from it. Sent over HTTP rather
               than left as a bubble that never resolves. */
            const res = await request('post', URLS.chatSendMessage, wire);
            if (res && res.status === 200) settle(res.data, false);
            else settle(null, true);
        }

        refreshConversations();
    }, [myCode, refreshConversations]);

    /* Put the bytes on the server and hand back what a message needs to point
       at them. Sending happens separately, over the socket like any other
       message, so a slow upload never holds the connection open. */
    const uploadImage = useCallback(async (file, onProgress) => {
        const { width, height, previewUrl } = await measureImage(file);

        const form = new FormData();
        form.append('image', file);

        try {
            /* No Content-Type is set on purpose. multipart needs a boundary
               in the header, and only the browser knows the one it generated
               for this FormData — naming the type by hand omits it and the
               server cannot then find where the file begins. */
            const res = await axios.post(BASE + URLS.chatUploadImage, form, {
                onUploadProgress: (event) => {
                    if (!onProgress || !event.total) return;
                    onProgress(Math.round((event.loaded * 100) / event.total));
                },
            });
            if (!res.data || res.data.status !== 200) {
                URL.revokeObjectURL(previewUrl);
                return { error: (res.data && res.data.message) || 'The image could not be sent' };
            }
            return { attachment: { ...res.data.data, width, height }, previewUrl };
        } catch (error) {
            URL.revokeObjectURL(previewUrl);
            const payload = error && error.response && error.response.data;
            return { error: (payload && payload.message) || 'The image could not be sent' };
        }
    }, []);

    /* ------------------------- editing and deleting ------------------------- */

    /* Both amends replace the bubble outright rather than merging into it.

       A delete arrives as a message with no text and no attachment, and those
       fields are absent from the payload rather than empty in it — merged,
       the old text would simply survive underneath and the bubble would go on
       showing words that have been taken back. */
    const applyAmend = useCallback((conversationId, saved) => {
        setThreads((prev) => {
            const current = prev[conversationId];
            if (!current) return prev;
            const id = String(saved._id);
            if (!current.messages.some((m) => String(m._id) === id)) return prev;

            return {
                ...prev,
                [conversationId]: {
                    ...current,
                    messages: current.messages.map((m) => {
                        if (String(m._id) === id) return { ...saved };

                        /* A reply carries its own copy of what it quotes, so
                           deleting a message has to reach those copies as
                           well — otherwise the words just taken back would go
                           on being shown underneath every answer to them. The
                           server writes the same correction to the stored
                           replies; this is what makes it visible now rather
                           than on the next load. */
                        if (saved.deleted && m.replyTo && String(m.replyTo.messageId) === id) {
                            return {
                                ...m,
                                replyTo: { ...m.replyTo, text: 'This message was deleted', kind: 'text' },
                            };
                        }
                        return m;
                    }),
                },
            };
        });
    }, []);

    const amend = useCallback(async (event, conversationId, message, body) => {
        const socket = socketRef.current;

        const done = (ok, saved, failure) => {
            if (ok && saved) applyAmend(conversationId, saved);
            refreshConversations();
            return { success: Boolean(ok), message: failure };
        };

        if (socket && socket.connected) {
            const ack = await emitWithAck(socket, event, { messageId: message._id, ...body });
            /* Only a silent socket falls through to HTTP. A refusal that came
               back properly is the answer, and repeating it over HTTP would
               earn the same refusal twice. */
            if (ack) return done(ack.success, ack.data, ack.message);
        }

        const res = event === 'chat:edit'
            ? await request('patch', URLS.chatEditMessage(message._id), body)
            : await request('delete', URLS.chatDeleteMessage(message._id));
        return done(res && res.status === 200, res && res.data, res && res.message);
    }, [refreshConversations, applyAmend]);

    const editMessage = useCallback(
        (conversationId, message, text) => amend('chat:edit', conversationId, message, { text }),
        [amend],
    );

    const deleteMessage = useCallback(
        (conversationId, message) => amend('chat:delete', conversationId, message, {}),
        [amend],
    );

    const retryMessage = useCallback((conversationId, peerCode, message) => {
        setThreads((prev) => {
            const current = prev[conversationId];
            if (!current) return prev;
            return {
                ...prev,
                [conversationId]: {
                    ...current,
                    messages: current.messages.filter((m) => m._id !== message._id),
                },
            };
        });
        sendMessage(conversationId, peerCode, message.text, {
            attachment: message.attachment || null,
            previewUrl: message.previewUrl || '',
            replyTo: message.replyTo
                ? { _id: message.replyTo.messageId, ...message.replyTo }
                : null,
        });
    }, [sendMessage]);

    /* Throttled: one "still typing" per couple of seconds is enough, and it
       keeps a fast typist from emitting on every keystroke. */
    const notifyTyping = useCallback((peerCode, conversationId) => {
        const socket = socketRef.current;
        if (!socket || !socket.connected || !peerCode) return;
        const now = Date.now();
        if (now - sentTypingAtRef.current < 1800) return;
        sentTypingAtRef.current = now;
        socket.emit('chat:typing', { peerCode, conversation: conversationId, typing: true });
    }, []);

    const stopTyping = useCallback((peerCode, conversationId) => {
        const socket = socketRef.current;
        if (!socket || !socket.connected || !peerCode) return;
        sentTypingAtRef.current = 0;
        socket.emit('chat:typing', { peerCode, conversation: conversationId, typing: false });
    }, []);

    /* ------------------------- presence ------------------------- */

    const trackPresence = useCallback(async (codes = []) => {
        const wanted = codes.filter(Boolean);
        if (!wanted.length) return;
        const res = await request('get', URLS.chatPresence(wanted));
        if (res && res.status === 200) setPresence((prev) => ({ ...prev, ...res.data }));
    }, []);

    /* ------------------------- the socket ------------------------- */

    useEffect(() => {
        if (!myCode) return undefined;

        /* Same origin, under the API prefix. In production nginx already
           forwards that prefix to the API, and the dev server proxies it, so
           real-time needs no extra routing of its own. If a proxy in the way
           refuses the websocket upgrade, socket.io stays on long-polling and
           chat keeps working. */
        const socket = io({
            path: '/api/v1/socket.io',
            withCredentials: true,
            reconnectionDelay: 800,
            reconnectionDelayMax: 6000,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            setConnected(true);
            /* Anything missed while the socket was down is picked up here,
               which is what makes a dropped connection recoverable rather
               than leaving the list quietly stale. */
            refreshConversations();
            const active = activeConversationRef.current;
            if (active) loadThread(active, { force: true });
            windowsRef.current.forEach((w) => loadThread(w.conversationId, { force: true }));
        });
        socket.on('disconnect', () => setConnected(false));

        socket.on('presence:update', ({ employeeCode, online, lastSeen }) => {
            setPresence((prev) => ({ ...prev, [employeeCode]: { online, lastSeen } }));
        });

        socket.on('chat:message', (message) => {
            const conversationId = String(message.conversation);
            const mine = message.senderCode === myCode;

            setThreads((prev) => {
                const current = prev[conversationId] || { messages: [], hasMore: true, loaded: false };
                /* Merged rather than pushed, so a message that is already
                   there — the sender's own optimistic copy, or one this tab
                   has seen — is updated instead of duplicated. */
                return {
                    ...prev,
                    [conversationId]: { ...current, messages: mergeMessages(current.messages, [message]) },
                };
            });

            /* They have clearly stopped typing now that it has arrived. */
            if (!mine) setTypingBy((prev) => ({ ...prev, [message.senderCode]: false }));

            refreshConversations();

            if (mine) return;

            const isOnScreen = activeConversationRef.current === conversationId;
            const openWindow = windowsRef.current.find((w) => w.conversationId === conversationId);

            if (isOnScreen) {
                markRead(conversationId);
                return;
            }

            if (openWindow) {
                setWindows((prev) => prev.map((w) => (w.conversationId === conversationId
                    ? { ...w, unread: w.minimised ? (w.unread || 0) + 1 : 0 } : w)));
                if (!openWindow.minimised) markRead(conversationId);
                return;
            }

            /* Nothing on screen is showing this thread, so the message opens
               its own window — the popup an employee gets when someone
               messages them from another page. */
            loadThread(conversationId);
            setWindows((prev) => {
                const next = [...prev, {
                    conversationId,
                    peer: {
                        employeeCode: message.senderCode,
                        employeeName: message.senderName || message.senderCode,
                    },
                    minimised: false,
                    unread: 0,
                    justArrived: true,
                }];
                return next.slice(-MAX_OPEN_WINDOWS);
            });
            markRead(conversationId);
        });

        /* An edit or a delete, from the other person or from another tab of
           this same employee. Replaced outright rather than merged, for the
           reason given on applyAmend. */
        socket.on('chat:updated', (message) => {
            applyAmend(String(message.conversation), message);
            refreshConversations();
        });

        socket.on('chat:delivered', ({ conversation, messageIds }) => {
            const ids = new Set((messageIds || []).map(String));
            setThreads((prev) => {
                const current = prev[String(conversation)];
                if (!current) return prev;
                return {
                    ...prev,
                    [String(conversation)]: {
                        ...current,
                        messages: current.messages.map((m) => (
                            ids.has(String(m._id)) && m.status === 'sent'
                                ? { ...m, status: 'delivered' } : m
                        )),
                    },
                };
            });
        });

        socket.on('chat:read', ({ conversation, messageIds, byCode }) => {
            const ids = new Set((messageIds || []).map(String));
            setThreads((prev) => {
                const current = prev[String(conversation)];
                if (!current) return prev;
                return {
                    ...prev,
                    [String(conversation)]: {
                        ...current,
                        messages: current.messages.map((m) => (
                            ids.has(String(m._id)) ? { ...m, status: 'read' } : m
                        )),
                    },
                };
            });
            if (byCode === myCode) {
                setConversations((prev) => prev.map((c) => (
                    c.conversationId === String(conversation) ? { ...c, unread: 0 } : c
                )));
            }
        });

        socket.on('chat:typing', ({ fromCode, typing }) => {
            setTypingBy((prev) => ({ ...prev, [fromCode]: Boolean(typing) }));
            clearTimeout(typingTimersRef.current[fromCode]);
            if (typing) {
                /* A "stopped typing" can be lost if the other tab closes, so
                   the indicator times itself out rather than sticking. */
                typingTimersRef.current[fromCode] = setTimeout(() => {
                    setTypingBy((prev) => ({ ...prev, [fromCode]: false }));
                }, 4000);
            }
        });

        return () => {
            Object.values(typingTimersRef.current).forEach(clearTimeout);
            typingTimersRef.current = {};
            socket.removeAllListeners();
            socket.close();
            socketRef.current = null;
        };
    }, [myCode, loadThread, markRead, refreshConversations, applyAmend]);

    useEffect(() => {
        if (myCode) refreshConversations();
    }, [myCode, refreshConversations]);

    const value = useMemo(() => ({
        myCode,
        connected,
        conversations,
        presence,
        threads,
        typingBy,
        windows,
        unreadTotal,
        openChatWith,
        closeWindow,
        minimiseWindow,
        sendMessage,
        retryMessage,
        editMessage,
        deleteMessage,
        uploadImage,
        loadThread,
        loadOlder,
        markRead,
        setActiveConversation,
        notifyTyping,
        stopTyping,
        trackPresence,
        refreshConversations,
    }), [
        myCode, connected, conversations, presence, threads, typingBy, windows, unreadTotal,
        openChatWith, closeWindow, minimiseWindow, sendMessage, retryMessage, loadThread,
        loadOlder, markRead, setActiveConversation, notifyTyping, stopTyping, trackPresence,
        refreshConversations, editMessage, deleteMessage, uploadImage,
    ]);

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext;
